import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import PhoenixLoader from 'components/common/PhoenixLoader';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { AppDispatch, RootState } from 'store';
import { Badge, Button } from 'react-bootstrap';
import {
  fetchApprovedRequests,
  selectApprovedRequests,
  selectPendingLoading,
  assignAssetToRequest
} from 'store/approvalsSlice';
import {
  putOnHoldThunk,
  resumeRequestThunk,
  cancelRequestThunk
} from 'store/allRequestsSlice';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ViewPurchaseModal from 'components/modals/ViewPurchaseModal';
import AssignAssetModal from 'components/modals/AssignAssetModal';
import PutOnHoldModal from 'components/modals/PutOnHoldModal';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faPlus,
  faBan,
  faBuilding,
  faUser,
  faUserCheck,
  faTag,
  faExclamationCircle,
  faCheckCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import DeleteAllRequestModal from 'components/modals/DeleteAllRequestModal';

interface DepartmentRequest {
  id: number;
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: string;
  user_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  asset_name: string;
  category_name: string;
  company_name: string;
  approver_name: string;
  departmentName: string;
  requestId: number;
  createdAt: string;
}

interface PurchaseTableProps {
  company_id?: number;
  requests?: DepartmentRequest[];
}

const PurchaseTable: React.FC<PurchaseTableProps> = ({
  company_id,
  requests: externalRequests
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { id, name } = useSelector((state: RootState) => state.auth);
  const reduxRequests = useSelector(selectApprovedRequests);
  const loading = useSelector(selectPendingLoading);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPutOnHoldModal, setShowPutOnHoldModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<DepartmentRequest | null>(null);

  // Use external requests if provided, otherwise use Redux
  const requests = externalRequests || reduxRequests;

  useEffect(() => {
    // Only fetch if company_id is provided and no external requests
    if (company_id && !externalRequests) {
      dispatch(fetchApprovedRequests({ company_id }));
    }
  }, [dispatch, company_id, externalRequests]);

  const handleAssignAsset = async (request_id: number, asset_id: number) => {
    // Validate that asset_id exists
    if (!asset_id || asset_id === 0) {
      alert(
        'This request does not have an asset ID. Please check the request details.'
      );
      return;
    }

    try {
      setLoadingId(request_id);
      await dispatch(assignAssetToRequest({ request_id, asset_id })).unwrap();

      // Refresh the approved requests list
      if (company_id) {
        dispatch(fetchApprovedRequests({ company_id }));
      }
    } catch (error) {
      console.error('Failed to assign asset:', error);
      alert('Failed to assign asset. Please try again.');
    } finally {
      setLoadingId(null);
    }
  };

  const handleViewRequest = (request: DepartmentRequest) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  const handleAddAsset = (request: DepartmentRequest) => {
    setSelectedRequest(request);
    setShowAssignModal(true);
  };

  // Handle Cancel Request
  const handleCancelRequest = (request: DepartmentRequest) => {
    setSelectedRequest(request);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (reason?: string) => {
    if (!selectedRequest) return;

    try {
      setLoadingId(selectedRequest.id);
      await dispatch(
        cancelRequestThunk({
          requestId: selectedRequest.id,
          cancellationReason: reason
        })
      ).unwrap();

      setShowCancelModal(false);
      setSelectedRequest(null);

      // Refresh the requests list after cancellation
      if (company_id) {
        dispatch(fetchApprovedRequests({ company_id }));
      }
    } catch (error) {
      console.error('Failed to cancel request:', error);
    } finally {
      setLoadingId(null);
    }
  };

  // Handle Put On Hold
  const handlePutOnHold = (request: DepartmentRequest) => {
    setSelectedRequest(request);
    setShowPutOnHoldModal(true);
  };

  const handlePutOnHoldConfirm = async (reason: string) => {
    if (!selectedRequest) return;

    setLoadingId(selectedRequest.id);
    try {
      await dispatch(
        putOnHoldThunk({
          requestId: selectedRequest.id,
          payload: { hold_reason: reason }
        })
      ).unwrap();

      setShowPutOnHoldModal(false);
      setSelectedRequest(null);

      // Refresh the requests list after putting on hold
      if (company_id) {
        await dispatch(fetchApprovedRequests({ company_id })).unwrap();
      } else {
        // Super Admin: reload page
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to put request on hold:', error);
    } finally {
      setLoadingId(null);
    }
  };

  // Handle Resume
  const handleResume = async (request: DepartmentRequest) => {
    setLoadingId(request.id);
    try {
      await dispatch(resumeRequestThunk(request.id)).unwrap();

      // Refresh the requests list after resuming
      if (company_id) {
        await dispatch(fetchApprovedRequests({ company_id })).unwrap();
      } else {
        // Super Admin: reload page
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to resume request:', error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleAssignSuccess = () => {
    // Refresh the requests list after successful assignment
    if (company_id) {
      dispatch(fetchApprovedRequests({ company_id }));
    }
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'success', label: 'Low' },
      medium: { variant: 'warning', label: 'Medium' },
      high: { variant: 'danger', label: 'High' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      variant: 'secondary',
      label: priority
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      approved: { variant: 'success', label: 'Approved' },
      'on-hold': { variant: 'secondary', label: 'On Hold' },
      completed: { variant: 'info', label: 'Completed' },
      cancelled: { variant: 'danger', label: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'secondary',
      label: status
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = useMemo<ColumnDef<DepartmentRequest>[]>(
    () => [
      {
        id: 'sn',
        header: 'SN',
        cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
        meta: {
          headerProps: {
            style: { width: '80px' }
          },
          cellProps: {}
        }
      },
      {
        accessorKey: 'company_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
            Company
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'user_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
            Requestor
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{getValue() as string}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'approver_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faUserCheck} className="me-2 text-primary" />
            Approver
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'category_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
            Category
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      // {
      //   accessorKey: 'priority',
      //   header: 'Priority',
      //   cell: ({ getValue }) => getPriorityBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3 text-uppercase'
      //     },
      //     cellProps: { className: 'ps-2' }
      //   }
      // },
      // {
      //   accessorKey: 'status',
      //   header: 'Status',
      //   cell: ({ getValue }) => getStatusBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3 text-uppercase'
      //     },
      //     cellProps: { className: 'ps-2' }
      //   }
      // },
      {
        accessorKey: 'priority',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="me-2 text-primary"
            />
            Priority
          </span>
        ),
        cell: ({ getValue }) => {
          const priority = (getValue() as string)?.toLowerCase();

          const config: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            high: {
              className: 'badge-phoenix-danger',
              icon: 'arrow-up',
              label: 'High'
            },
            medium: {
              className: 'badge-phoenix-warning',
              icon: 'minus',
              label: 'Medium'
            },
            low: {
              className: 'badge-phoenix-success',
              icon: 'arrow-down',
              label: 'Low'
            }
          };

          const badge = config[priority] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: priority
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'status',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="me-2 text-primary"
            />
            Status
          </span>
        ),
        cell: ({ getValue }) => {
          const status = (getValue() as string)?.toLowerCase();

          // Map status to Phoenix badge color and professional icon
          const statusConfig: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            fulfilled: {
              className: 'badge-phoenix-success', // green
              icon: 'check-circle', // professional “completed” icon
              label: 'Fulfilled'
            },
            'on-hold': {
              className: 'badge-phoenix-warning', // yellow
              icon: 'pause-circle', // professional “on-hold” icon
              label: 'On Hold'
            },
            approved: {
              className: 'badge-phoenix-primary', // blue
              icon: 'thumbs-up', // professional “approved” icon
              label: 'Approved'
            },
            rejected: {
              className: 'badge-phoenix-danger', // red
              icon: 'x-circle', // professional “rejected” icon
              label: 'Rejected'
            },
            pending: {
              className: 'badge-phoenix-info', // cyan/neutral
              icon: 'clock', // professional “pending” icon
              label: 'Pending'
            }
          };

          const badge = statusConfig[status] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: status
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'created_at',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="me-2 text-primary"
            />
            Date
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{formatDate(getValue() as string)}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        id: 'actions',
        header: () => (
          <div className="d-inline-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
            Actions
          </div>
        ),
        cell: ({ row: { original } }) => (
          <>
            {loadingId === original.id ? (
              <PhoenixLoader />
            ) : (
              <RevealDropdownTrigger>
                <RevealDropdown>
                  <Dropdown.Item onClick={() => handleViewRequest(original)}>
                    <FontAwesomeIcon icon={faEye} className="me-2 text-info" />
                    View
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleAddAsset(original)}>
                    <FontAwesomeIcon
                      icon={faPlus}
                      className="me-2 text-success"
                    />
                    Add Asset
                  </Dropdown.Item>
                  <Dropdown.Divider />

                  {/* Put On Hold - only for approved */}
                  {original.status === 'approved' && (
                    <Dropdown.Item onClick={() => handlePutOnHold(original)}>
                      <FeatherIcon
                        icon="pause-circle"
                        size={16}
                        className="me-2"
                      />
                      Put On Hold
                    </Dropdown.Item>
                  )}

                  {/* Resume - only for on-hold, highlighted in green */}
                  {original.status === 'on-hold' && (
                    <Dropdown.Item
                      onClick={() => handleResume(original)}
                      className="text-success fw-normal"
                    >
                      <FeatherIcon
                        icon="play-circle"
                        size={16}
                        className="me-2 text-success"
                      />
                      Resume
                    </Dropdown.Item>
                  )}

                  {(original.status === 'approved' ||
                    original.status === 'on-hold') && <Dropdown.Divider />}

                  {/* Cancel Button - for approved and on-hold */}
                  {(original.status === 'approved' ||
                    original.status === 'on-hold') && (
                    <Dropdown.Item
                      onClick={() => handleCancelRequest(original)}
                      className="text-warning"
                    >
                      <FontAwesomeIcon icon={faBan} className="me-2" />
                      Cancel
                    </Dropdown.Item>
                  )}
                </RevealDropdown>
              </RevealDropdownTrigger>
            )}
          </>
        ),
        meta: {
          headerProps: {
            className: 'text-end pe-3'
          },
          cellProps: { className: 'text-end pe-3' }
        }
      }
    ],
    [loadingId]
  );

  const table = useAdvanceTable({
    data: requests,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  if (loading && !externalRequests) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <PhoenixLoader />
      </div>
    );
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div
          className="bg-white"
          style={{
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}
        >
          <div className="table-responsive" style={{ overflowY: 'visible' }}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table mb-0',
                hover: true
              }}
              headerClassName=""
              rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
            />
          </div>
          <div className="table-footer-elegant">
            <AdvanceTableFooter pagination className="px-3 py-2" />
          </div>
        </div>
      </AdvanceTableProvider>

      <ViewPurchaseModal
        show={showViewModal}
        handleClose={() => {
          setShowViewModal(false);
          setSelectedRequest(null);
        }}
        purchase={selectedRequest}
      />

      <AssignAssetModal
        show={showAssignModal}
        handleClose={() => {
          setShowAssignModal(false);
          setSelectedRequest(null);
        }}
        requestId={selectedRequest?.id || 0}
        categoryId={selectedRequest?.category_id || 0}
        requestInfo={{
          asset_name: selectedRequest?.asset_name,
          user_name: selectedRequest?.user_name,
          category_name: selectedRequest?.category_name,
          company_id: selectedRequest?.company_id,
          company_name: selectedRequest?.company_name
        }}
        onAssignSuccess={handleAssignSuccess}
      />

      {/* Put On Hold Modal */}
      <PutOnHoldModal
        show={showPutOnHoldModal}
        handleClose={() => {
          setShowPutOnHoldModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onPutOnHold={handlePutOnHoldConfirm}
      />

      {/* Cancel Request Modal */}
      {selectedRequest && (
        <DeleteAllRequestModal
          show={showCancelModal}
          handleClose={() => {
            setShowCancelModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onCancel={handleCancelConfirm}
          isAdmin={true}
        />
      )}
    </>
  );
};

export default PurchaseTable;
